import * as React from 'react';

import styled from '@emotion/styled';
import { flatten, union, uniq, xor } from 'lodash';

import { Checkbox } from '@coursera/cds-core';
import { Box } from '@coursera/coursera-ui';

import type { FunctionMapping, OccupationMapping } from 'bundles/enterprise-admin-skills/components/types';
import { formatDepartmentName, formatOccupationName } from 'bundles/enterprise-admin-skills/components/utils';
import { Labeled, Spacer } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/enterprise-admin-skills';

const Academies = styled.div({
  padding: '18px',
});

const Department = styled(Labeled)({
  background: 'var(--cds-color-grey-25)',
  padding: '12px 18px',
});

const Occupation = styled(Labeled)({
  width: '50%',
  marginBottom: '12px',
});

type Props = {
  selectedAcademyIds: string[];
  selectedDepartmentIds: string[];
  selectedOccupationIds: string[];
  onOccupationIdsChange: (ids: string[]) => void;
  onDepartmentIdsChange: (ids: string[]) => void;
  skillFinderData: {
    department: string;
    academies: { academy: string; occupations: string[] }[];
  }[];
  occupationMappings?: OccupationMapping[];
  functionMappings?: FunctionMapping[];
};

export default class BusinessAreaAndRoleSelection extends React.Component<Props> {
  handleDepartmentChange =
    ({
      departmentOccupations,
      department,
      isDepartmentChecked,
    }: {
      departmentOccupations: string[];
      department: string;
      isDepartmentChecked: boolean;
    }) =>
    () => {
      const { onDepartmentIdsChange, selectedDepartmentIds, selectedOccupationIds, onOccupationIdsChange } = this.props;

      onOccupationIdsChange(selectedOccupationIds.filter((_) => !departmentOccupations.includes(_)));
      onDepartmentIdsChange(
        isDepartmentChecked
          ? selectedDepartmentIds.filter((_) => _ !== department)
          : union(selectedDepartmentIds, [department])
      );
    };

  handleOccupationChange =
    ({
      isOccupationChecked,
      isDepartmentChecked,
      department,
      occupation,
      departmentOccupations,
    }: {
      isOccupationChecked: boolean;
      isDepartmentChecked: boolean;
      department: string;
      occupation: string;
      departmentOccupations: string[];
    }) =>
    () => {
      const { onDepartmentIdsChange, selectedDepartmentIds, selectedOccupationIds, onOccupationIdsChange } = this.props;
      if (isOccupationChecked) {
        if (isDepartmentChecked) {
          onDepartmentIdsChange(selectedDepartmentIds.filter((_) => _ !== department));
          onOccupationIdsChange(union(selectedOccupationIds, xor(departmentOccupations, [occupation])));
        } else {
          onOccupationIdsChange(xor(selectedOccupationIds, [occupation]));
        }
      } else {
        const shouldDepartmentBeChecked = departmentOccupations
          .filter((_) => _ !== occupation)
          .every((_) => selectedOccupationIds.includes(_));
        if (shouldDepartmentBeChecked) {
          onDepartmentIdsChange(union(selectedDepartmentIds, [department]));
          onOccupationIdsChange(selectedOccupationIds.filter((_) => !departmentOccupations.includes(_)));
        } else {
          onOccupationIdsChange(union(selectedOccupationIds, [occupation]));
        }
      }
    };

  render() {
    const {
      selectedAcademyIds,
      selectedDepartmentIds,
      selectedOccupationIds,
      skillFinderData,
      occupationMappings,
      functionMappings,
    } = this.props;
    return (
      <div>
        {skillFinderData
          .filter((_) => _.academies.some((__) => selectedAcademyIds.includes(__.academy)) && _.department)

          .map((departmentGroup) => {
            const { department, academies } = departmentGroup;
            const filteredAcademies = academies.filter((_) => selectedAcademyIds.includes(_.academy));
            const departmentOccupations = filteredAcademies
              .map((_) => _.occupations)
              .reduce((a, b) => [...a, ...b], []);

            const isDepartmentChecked = selectedDepartmentIds.includes(department);

            return (
              <div key={department}>
                <Department>
                  <Checkbox
                    checked={isDepartmentChecked}
                    onChange={this.handleDepartmentChange({
                      departmentOccupations,
                      department,
                      isDepartmentChecked,
                    })}
                    data-e2e={department}
                    label={formatDepartmentName(department, functionMappings) ?? _t('Unknown department')}
                  />
                </Department>
                <Academies>
                  <Box flexWrap="wrap">
                    {uniq(flatten(filteredAcademies.map((_) => _.occupations))).map((occupation) => {
                      const checked = isDepartmentChecked || selectedOccupationIds.includes(occupation);
                      return (
                        <Occupation key={occupation}>
                          <Checkbox
                            checked={checked}
                            onChange={this.handleOccupationChange({
                              isOccupationChecked: checked,
                              isDepartmentChecked,
                              department,
                              occupation,
                              departmentOccupations,
                            })}
                            data-e2e={occupation}
                            label={formatOccupationName(occupation, occupationMappings) ?? _t('Unknown occupation')}
                          />
                        </Occupation>
                      );
                    })}
                  </Box>
                </Academies>
                <Spacer size={24} />
              </div>
            );
          })}
      </div>
    );
  }
}
